/**
 * Date utility functions
 */

import { formatDistanceToNow } from 'date-fns'

/**
 * Safely formats a UTC date string to relative time (e.g., "2 hours ago")
 * Handles UTC dates correctly by checking for timezone info and adding 'Z' if needed
 * @param dateString ISO date string (assumed to be UTC if no timezone info)
 * @returns Formatted relative time string with "ago" suffix
 */
export function safeFormatDistanceToNow(dateString: string | undefined | null): string {
  if (!dateString) return 'recently'
  try {
    // Parse UTC date string and convert to local time
    // If the string ends with 'Z' or has timezone offset (+ or - after the time portion), it's already timezone-aware
    // Otherwise append 'Z' to treat it as UTC
    const hasTimezone = dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)
    const utcString = hasTimezone ? dateString : dateString + 'Z'
    const date = new Date(utcString)
    if (isNaN(date.getTime())) return 'recently'
    return formatDistanceToNow(date, { addSuffix: true })
  } catch {
    return 'recently'
  }
}

/**
 * Formats a date as relative time (e.g., "2 hours ago", "3 days ago")
 * @param dateString ISO date string
 * @returns Formatted relative time string
 */
export function formatRelativeTime(dateString: string | undefined): string {
  if (!dateString) return ''

  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffMonths = Math.floor(diffDays / 30)
  const diffYears = Math.floor(diffDays / 365)

  if (diffYears > 0) {
    return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`
  } else if (diffMonths > 0) {
    return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`
  } else if (diffDays > 0) {
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`
  } else if (diffHours > 0) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`
  } else if (diffMinutes > 0) {
    return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`
  } else {
    return 'Just now'
  }
}
